'use client'

import { useState, useMemo, useEffect, useRef } from 'react'
import nextDynamic from 'next/dynamic'
import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { divingPools, getMapCenter } from '@/data/divingPools'
import type { DivingPool } from '@/data/divingPools'
import { seaSpots, getSeaMapCenter, SEA_REGION_COLORS } from '@/data/seaSpots'
import type { SeaSpot } from '@/data/seaSpots'
import type { Valley } from '@/data/valleys'
import {
  MapPin, Filter, X, Loader2, ChevronRight, Car, Plus,
} from 'lucide-react'

const KakaoMapLoader = nextDynamic(() => import('@/components/Pools/KakaoMapLoader'), { ssr: false })
const PoolMap = nextDynamic(() => import('@/components/Pools/PoolMap'), { ssr: false })
const ValleyMap = nextDynamic(() => import('@/components/Valleys/ValleyMap'), { ssr: false })
const SeaMap = nextDynamic(() => import('@/components/Spots/SeaMap'), { ssr: false })

type MapTab = 'pool' | 'valley' | 'sea'
type FilterLevel = 'all' | 'beginner' | 'intermediate' | 'advanced'
type FilterRegion = 'all' | '서울' | '경기' | '인천'
type FilterProvince = 'all' | '경기도' | '강원특별자치도' | '충청북도' | '경상북도' | '경상남도' | '전라남도' | '전라북도'
type FilterSeaRegion = 'all' | '동해안' | '서해안' | '남해안'

// ─────────────────────────────────────────────
// 공통 높이 상수
// desktop: header(~60px) + tab-bar(~48px) = 108px
// mobile:  header(~60px) + tab-bar(~48px) + bottom-nav(~56px) = 164px → 여유 172px
// ─────────────────────────────────────────────
const DESKTOP_H = 'calc(100vh - 108px)'
const MOBILE_H = 'calc(100dvh - 172px)'

// ─────────────────────────────────────────────
// 풀장 탭
// ─────────────────────────────────────────────
function PoolTab({ isDark }: { isDark: boolean }) {
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null)
  const [filterLevel, setFilterLevel] = useState<FilterLevel>('all')
  const [filterRegion, setFilterRegion] = useState<FilterRegion>('all')
  const [showFilters, setShowFilters] = useState(false)
  const mobileListRef = useRef<HTMLDivElement>(null)
  const desktopListRef = useRef<HTMLDivElement>(null)

  const filteredPools = useMemo(() =>
    divingPools.filter(p => {
      if (filterLevel !== 'all' && p.level !== filterLevel) return false
      if (filterRegion !== 'all' && p.region !== filterRegion) return false
      return true
    }),
    [filterLevel, filterRegion]
  )

  const mapCenter = useMemo(() => {
    if (selectedPoolId) {
      const p = divingPools.find(p => p.id === selectedPoolId)
      if (p) return { lat: p.lat, lng: p.lng }
    }
    return getMapCenter()
  }, [selectedPoolId])

  useEffect(() => {
    if (!selectedPoolId) return
    if (mobileListRef.current) {
      const el = mobileListRef.current.querySelector(`[data-pool-id="${selectedPoolId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    if (desktopListRef.current) {
      const el = desktopListRef.current.querySelector(`[data-pool-id="${selectedPoolId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedPoolId])

  const getLevelColor = (level: DivingPool['level']) => {
    switch (level) {
      case 'beginner': return isDark ? 'bg-[rgba(43,109,240,0.15)] text-[#5ea6ff]' : 'bg-[#e9eff7] text-[#2b6df0]'
      case 'intermediate': return isDark ? 'bg-[rgba(43,109,240,0.2)] text-[#7bbfff]' : 'bg-[#d6e4f7] text-[#143560]'
      case 'advanced': return isDark ? 'bg-[rgba(7,72,255,0.2)] text-[#93c5fd]' : 'bg-[#0a1f3a] text-white'
    }
  }
  const getLevelText = (level: DivingPool['level']) => {
    switch (level) {
      case 'beginner': return '초급'; case 'intermediate': return '중급'; case 'advanced': return '상급'
    }
  }

  const activeFilters = (filterLevel !== 'all' ? 1 : 0) + (filterRegion !== 'all' ? 1 : 0)

  const PoolCard = ({ pool, compact }: { pool: DivingPool; compact?: boolean }) => (
    <div
      data-pool-id={pool.id}
      onClick={() => setSelectedPoolId(pool.id)}
      className={`cursor-pointer transition-all rounded-xl ${
        selectedPoolId === pool.id
          ? isDark ? 'bg-[rgba(43,109,240,0.15)] shadow-[inset_0_0_0_1.5px_#2b6df0]' : 'bg-[#e9eff7] shadow-[inset_0_0_0_1.5px_#2b6df0]'
          : isDark ? 'bg-gray-800 shadow-[inset_0_0_0_1px_#2c3a52] hover:shadow-[inset_0_0_0_1px_#4a5a72]' : 'bg-white shadow-[inset_0_0_0_1px_#e9eff7] hover:shadow-[inset_0_0_0_1px_#c0cfe8]'
      } ${compact ? 'flex-shrink-0 w-[136px] p-2' : 'p-4'}`}
    >
      {compact ? (
        <>
          <div className="flex gap-1 mb-1 flex-wrap">
            <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${getLevelColor(pool.level)}`}>{getLevelText(pool.level)}</span>
            <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{pool.depth}</span>
          </div>
          <h3 className={`text-[11px] font-bold leading-tight line-clamp-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{pool.name}</h3>
          <p className={`text-[9px] mt-1 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pool.region}</p>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${getLevelColor(pool.level)}`}>{getLevelText(pool.level)}</span>
              <span className={`px-2 py-0.5 text-[10px] rounded ${isDark ? 'bg-[#2c3a52] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>{pool.region}</span>
            </div>
            <span className={`text-xs font-bold tabular-nums ${isDark ? 'text-[#5ea6ff]' : 'text-[#2b6df0]'}`}>{pool.depth}</span>
          </div>
          <h3 className={`font-bold text-sm mb-2 ${isDark ? 'text-[#e8f0fb]' : 'text-[#0a1f3a]'}`}>{pool.name}</h3>
          <div className={`space-y-1 text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
            <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{pool.address}</span></div>
            {pool.parkingInfo && <div className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{pool.parkingInfo}</span></div>}
          </div>
          {pool.features && pool.features.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {pool.features.slice(0, 3).map((f, i) => (
                <span key={i} className={`px-2 py-0.5 text-[10px] rounded-full ${isDark ? 'bg-[#2c3a52] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>{f}</span>
              ))}
            </div>
          )}
          <Link href={`/pools/${pool.id}`} onClick={e => e.stopPropagation()}
            className={`flex items-center gap-1 mt-3 text-xs font-medium ${isDark ? 'text-[#5ea6ff]' : 'text-[#2b6df0]'}`}>
            상세보기<ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </>
      )}
    </div>
  )

  return (
    <div>
      {/* 필터 영역 (데스크탑) */}
      <div className={`hidden md:block px-4 py-2 border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
        <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
          <span className={`text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>{filteredPools.length}개 풀장</span>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`relative flex items-center gap-1 px-3 py-1 text-xs rounded-lg ${showFilters || activeFilters > 0 ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>
            <Filter className="w-3.5 h-3.5" />필터
            {activeFilters > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 text-[9px] font-bold bg-[#0748ff] text-white rounded-full flex items-center justify-center">{activeFilters}</span>}
          </button>
          {showFilters && (
            <div className="flex items-center gap-2 flex-wrap">
              {(['all','beginner','intermediate','advanced'] as FilterLevel[]).map(v => (
                <button key={v} onClick={() => setFilterLevel(v)}
                  className={`px-2 py-0.5 text-xs rounded-lg ${filterLevel === v ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-white text-[#6b7684] shadow-[inset_0_0_0_1px_#e9eff7]'}`}>
                  {v === 'all' ? '전체' : v === 'beginner' ? '초급' : v === 'intermediate' ? '중급' : '상급'}
                </button>
              ))}
              <span className={`text-xs ${isDark ? 'text-[#2c3a52]' : 'text-[#e9eff7]'}`}>|</span>
              {(['all','서울','경기','인천'] as FilterRegion[]).map(v => (
                <button key={v} onClick={() => setFilterRegion(v)}
                  className={`px-2 py-0.5 text-xs rounded-lg ${filterRegion === v ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-white text-[#6b7684] shadow-[inset_0_0_0_1px_#e9eff7]'}`}>
                  {v === 'all' ? '전체' : v}
                </button>
              ))}
              {activeFilters > 0 && (
                <button onClick={() => { setFilterLevel('all'); setFilterRegion('all') }}
                  className={`flex items-center gap-0.5 text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
                  <X className="w-3 h-3" />초기화
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 모바일: 가로스크롤 카드 + 지도 */}
      <div className="md:hidden flex flex-col" style={{ height: MOBILE_H }}>
        <div ref={mobileListRef} className={`border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}
          style={{ height: '22%', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <div className="flex gap-2 px-3 py-2 h-full items-stretch">
            {filteredPools.length === 0
              ? <div className={`flex items-center justify-center w-full text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>조건에 맞는 풀장이 없습니다</div>
              : filteredPools.map(p => <PoolCard key={p.id} pool={p} compact />)
            }
          </div>
        </div>
        <div style={{ height: '78%', overflow: 'hidden' }}>
          <PoolMap pools={filteredPools} selectedPoolId={selectedPoolId} onPoolSelect={setSelectedPoolId} center={mapCenter} level={selectedPoolId ? 6 : 9} />
        </div>
      </div>

      {/* 데스크탑: 좌측 리스트 + 우측 지도 */}
      <div className="hidden md:flex flex-row max-w-7xl mx-auto" style={{ height: DESKTOP_H }}>
        <div ref={desktopListRef} className={`w-[400px] flex-shrink-0 overflow-y-auto border-r ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
          <div className="p-3 space-y-2">
            {filteredPools.length === 0
              ? <div className={`text-center py-12 ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}><MapPin className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">조건에 맞는 풀장이 없습니다</p></div>
              : filteredPools.map(p => <PoolCard key={p.id} pool={p} />)
            }
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <PoolMap pools={filteredPools} selectedPoolId={selectedPoolId} onPoolSelect={setSelectedPoolId} center={mapCenter} level={selectedPoolId ? 6 : 9} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// 계곡 탭
// ─────────────────────────────────────────────
function ValleyTab({ isDark }: { isDark: boolean }) {
  const [valleys, setValleys] = useState<Valley[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedValleyId, setSelectedValleyId] = useState<string | null>(null)
  const [filterProvince, setFilterProvince] = useState<FilterProvince>('all')
  const [showFilters, setShowFilters] = useState(false)
  const mobileListRef = useRef<HTMLDivElement>(null)
  const desktopListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetch('/api/valleys')
      .then(r => r.json())
      .then(json => {
        if (json.data) {
          setValleys(json.data.map((v: Record<string, unknown>) => ({
            id: v.id as string,
            name: v.name as string,
            address: v.address as string,
            province: v.province as string,
            city: v.city as string,
            avgDepth: (v.avg_depth as number) ?? 0,
            maxDepth: (v.max_depth as number) ?? 0,
            familyScore: (v.family_score as number) ?? 50,
            adultScore: (v.adult_score as number) ?? 50,
            rating: (v.rating as number) ?? 4,
            youtubeUrls: ((v.youtube_urls as string) || '').split(',').map((u: string) => u.trim()).filter(Boolean),
            latitude: v.latitude as number,
            longitude: v.longitude as number,
            weatherInfo: v.weather_info as string | undefined,
          })))
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const provinces: FilterProvince[] = ['all','경기도','강원특별자치도','충청북도','경상북도','경상남도','전라남도','전라북도']
  const provinceLabels: Record<FilterProvince, string> = { all:'전체', 경기도:'경기', 강원특별자치도:'강원', 충청북도:'충북', 경상북도:'경북', 경상남도:'경남', 전라남도:'전남', 전라북도:'전북' }

  const filteredValleys = useMemo(() =>
    filterProvince === 'all' ? valleys : valleys.filter(v => v.province === filterProvince),
    [filterProvince, valleys]
  )
  const mappableValleys = useMemo(() => filteredValleys.filter(v => !(v.latitude === 0 && v.longitude === 0)), [filteredValleys])

  const mapCenter = useMemo(() => {
    if (selectedValleyId) {
      const v = valleys.find(v => v.id === selectedValleyId)
      if (v && !(v.latitude === 0 && v.longitude === 0)) return { lat: v.latitude, lng: v.longitude }
    }
    if (mappableValleys.length === 0) return { lat: 36.8, lng: 127.9 }
    return { lat: mappableValleys.reduce((s, v) => s + v.latitude, 0) / mappableValleys.length, lng: mappableValleys.reduce((s, v) => s + v.longitude, 0) / mappableValleys.length }
  }, [selectedValleyId, mappableValleys, valleys])

  useEffect(() => {
    if (!selectedValleyId) return
    if (mobileListRef.current) {
      const el = mobileListRef.current.querySelector(`[data-valley-id="${selectedValleyId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    if (desktopListRef.current) {
      const el = desktopListRef.current.querySelector(`[data-valley-id="${selectedValleyId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedValleyId])

  if (loading) return (
    <div className={`flex items-center justify-center py-12 ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
      <Loader2 className="w-5 h-5 animate-spin mr-2" />계곡 정보를 불러오는 중...
    </div>
  )

  const ValleyCard = ({ valley, compact }: { valley: Valley; compact?: boolean }) => {
    const noCoords = valley.latitude === 0 && valley.longitude === 0
    return (
      <div
        data-valley-id={valley.id}
        onClick={() => setSelectedValleyId(valley.id)}
        className={`cursor-pointer transition-all border-2 rounded-xl ${
          selectedValleyId === valley.id
            ? isDark ? 'bg-[rgba(43,109,240,0.15)] shadow-[inset_0_0_0_1.5px_#2b6df0]' : 'bg-[#e9eff7] shadow-[inset_0_0_0_1.5px_#2b6df0]'
            : isDark ? 'bg-gray-800 shadow-[inset_0_0_0_1px_#2c3a52] hover:shadow-[inset_0_0_0_1px_#4a5a72]' : 'bg-white shadow-[inset_0_0_0_1px_#e9eff7] hover:shadow-[inset_0_0_0_1px_#c0cfe8]'
        } ${compact ? 'flex-shrink-0 w-[132px] p-2 flex flex-col justify-between' : 'p-4'}`}
      >
        {compact ? (
          <>
            <div>
              <div className="flex gap-1 mb-1 flex-wrap">
                <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{valley.city}</span>
                <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-[rgba(43,109,240,0.15)] text-[#5ea6ff]' : 'bg-[#d6e4f7] text-[#143560]'}`}>{valley.maxDepth}m</span>
                {noCoords && <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-600">미등록</span>}
              </div>
              <h3 className={`text-[11px] font-bold leading-tight line-clamp-2 ${isDark ? 'text-[#e8f0fb]' : 'text-[#0a1f3a]'}`}>{valley.name}</h3>
            </div>
            <div className="flex gap-1 mt-1 flex-wrap">
              <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-[rgba(43,109,240,0.12)] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>가족{valley.familyScore}</span>
              <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-[rgba(43,109,240,0.15)] text-[#5ea6ff]' : 'bg-[#d6e4f7] text-[#143560]'}`}>성인{valley.adultScore}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`px-2 py-0.5 text-[10px] rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{valley.city}</span>
                {noCoords && <span className="px-2 py-0.5 text-[10px] rounded bg-orange-100 text-orange-600">지도 미등록</span>}
              </div>
              <span className={`text-xs font-bold ${isDark ? 'text-[#5ea6ff]' : 'text-[#2b6df0]'}`}>최대 {valley.maxDepth}m</span>
            </div>
            <h3 className={`font-bold text-sm mb-2 ${isDark ? 'text-[#e8f0fb]' : 'text-[#0a1f3a]'}`}>{valley.name}</h3>
            <div className={`text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{valley.address}</span></div>
            </div>
            <div className="flex gap-2 mt-3">
              <span className={`px-2 py-0.5 text-[10px] rounded ${isDark ? 'bg-[rgba(43,109,240,0.12)] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>가족 {valley.familyScore}점</span>
              <span className={`px-2 py-0.5 text-[10px] rounded ${isDark ? 'bg-[rgba(43,109,240,0.15)] text-[#5ea6ff]' : 'bg-[#d6e4f7] text-[#143560]'}`}>성인 {valley.adultScore}점</span>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* 필터 영역 (데스크탑) */}
      <div className={`hidden md:block px-4 py-2 border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
        <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
          <span className={`text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>{filteredValleys.length}개 계곡</span>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`relative flex items-center gap-1 px-3 py-1 text-xs rounded-lg ${showFilters || filterProvince !== 'all' ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>
            <Filter className="w-3.5 h-3.5" />필터
            {filterProvince !== 'all' && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 text-[9px] font-bold bg-[#0748ff] text-white rounded-full flex items-center justify-center">1</span>}
          </button>
          {showFilters && (
            <div className="flex items-center gap-2 flex-wrap">
              {provinces.map(p => (
                <button key={p} onClick={() => setFilterProvince(p)}
                  className={`px-2 py-0.5 text-xs rounded-lg ${filterProvince === p ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-white text-[#6b7684] shadow-[inset_0_0_0_1px_#e9eff7]'}`}>
                  {provinceLabels[p]}
                </button>
              ))}
              {filterProvince !== 'all' && (
                <button onClick={() => setFilterProvince('all')} className={`flex items-center gap-0.5 text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
                  <X className="w-3 h-3" />초기화
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 모바일 */}
      <div className="md:hidden flex flex-col" style={{ height: MOBILE_H }}>
        <div ref={mobileListRef} className={`border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}
          style={{ height: '22%', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <div className="flex gap-2 px-3 py-2 h-full items-stretch">
            {filteredValleys.length === 0
              ? <div className={`flex items-center justify-center w-full text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>조건에 맞는 계곡이 없습니다</div>
              : filteredValleys.map(v => <ValleyCard key={v.id} valley={v} compact />)
            }
          </div>
        </div>
        <div style={{ height: '78%', overflow: 'hidden' }}>
          <ValleyMap valleys={mappableValleys} selectedValleyId={selectedValleyId} onValleySelect={setSelectedValleyId} center={mapCenter} level={selectedValleyId ? 7 : 8} />
        </div>
      </div>

      {/* 데스크탑 */}
      <div className="hidden md:flex flex-row max-w-7xl mx-auto" style={{ height: DESKTOP_H }}>
        <div ref={desktopListRef} className={`w-[400px] flex-shrink-0 overflow-y-auto border-r ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
          <div className="p-3 space-y-2">
            {filteredValleys.length === 0
              ? <div className={`text-center py-12 ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}><MapPin className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">조건에 맞는 계곡이 없습니다</p></div>
              : filteredValleys.map(v => <ValleyCard key={v.id} valley={v} />)
            }
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <ValleyMap valleys={mappableValleys} selectedValleyId={selectedValleyId} onValleySelect={setSelectedValleyId} center={mapCenter} level={selectedValleyId ? 7 : 8} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// 바다 탭
// ─────────────────────────────────────────────
function SeaTab({ isDark }: { isDark: boolean }) {
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null)
  const [filterRegion, setFilterRegion] = useState<FilterSeaRegion>('all')
  const mobileListRef = useRef<HTMLDivElement>(null)
  const desktopListRef = useRef<HTMLDivElement>(null)

  const filteredSpots = useMemo(() =>
    filterRegion === 'all' ? seaSpots : seaSpots.filter(s => s.region === filterRegion),
    [filterRegion]
  )

  const mapCenter = useMemo(() => {
    if (selectedSpotId) {
      const s = seaSpots.find(s => s.id === selectedSpotId)
      if (s) return { lat: s.latitude, lng: s.longitude }
    }
    return getSeaMapCenter(filteredSpots)
  }, [selectedSpotId, filteredSpots])

  useEffect(() => {
    if (!selectedSpotId) return
    if (mobileListRef.current) {
      const el = mobileListRef.current.querySelector(`[data-spot-id="${selectedSpotId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    if (desktopListRef.current) {
      const el = desktopListRef.current.querySelector(`[data-spot-id="${selectedSpotId}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedSpotId])

  const SeaCard = ({ spot, compact }: { spot: SeaSpot; compact?: boolean }) => {
    const color = SEA_REGION_COLORS[spot.region]
    return (
      <div
        data-spot-id={spot.id}
        onClick={() => setSelectedSpotId(spot.id)}
        className={`cursor-pointer transition-all border-2 rounded-xl ${
          selectedSpotId === spot.id
            ? isDark ? 'bg-[rgba(43,109,240,0.15)] shadow-[inset_0_0_0_1.5px_#2b6df0]' : 'bg-[#e9eff7] shadow-[inset_0_0_0_1.5px_#2b6df0]'
            : isDark ? 'bg-gray-800 shadow-[inset_0_0_0_1px_#2c3a52] hover:shadow-[inset_0_0_0_1px_#4a5a72]' : 'bg-white shadow-[inset_0_0_0_1px_#e9eff7] hover:shadow-[inset_0_0_0_1px_#c0cfe8]'
        } ${compact ? 'flex-shrink-0 w-[132px] p-2 flex flex-col justify-between' : 'p-4'}`}
      >
        {compact ? (
          <>
            <div>
              <div className="flex gap-1 mb-1 flex-wrap">
                <span className="text-[9px] px-1 py-0.5 rounded font-bold text-white" style={{ backgroundColor: color }}>{spot.region === '동해안' ? '동해' : spot.region === '서해안' ? '서해' : '남해'}</span>
                <span className={`text-[9px] px-1 py-0.5 rounded ${isDark ? 'bg-[rgba(43,109,240,0.15)] text-[#5ea6ff]' : 'bg-[#d6e4f7] text-[#143560]'}`}>{spot.depth}</span>
              </div>
              <h3 className={`text-[11px] font-bold leading-tight line-clamp-2 ${isDark ? 'text-[#e8f0fb]' : 'text-[#0a1f3a]'}`}>{spot.name}</h3>
            </div>
            <p className={`text-[9px] mt-1 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{spot.level}</p>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between mb-2">
              <span className="px-2 py-0.5 text-[10px] font-bold rounded text-white" style={{ backgroundColor: color }}>{spot.region}</span>
              <span className={`text-xs font-bold ${isDark ? 'text-[#5ea6ff]' : 'text-[#2b6df0]'}`}>{spot.depth}</span>
            </div>
            <h3 className={`font-bold text-sm mb-2 ${isDark ? 'text-[#e8f0fb]' : 'text-[#0a1f3a]'}`}>{spot.name}</h3>
            <div className={`text-xs space-y-1 ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{spot.address}</span></div>
              <p>⭐ {spot.level}</p>
            </div>
            {spot.features.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {spot.features.map((f, i) => (
                  <span key={i} className={`px-2 py-0.5 text-[10px] rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{f}</span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* 필터 영역 (데스크탑) */}
      <div className={`hidden md:block px-4 py-2 border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
        <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
          <span className={`text-xs ${isDark ? 'text-[#94a3b8]' : 'text-[#6b7684]'}`}>{filteredSpots.length}개 포인트</span>
          {(['all','동해안','서해안','남해안'] as FilterSeaRegion[]).map(r => (
            <button key={r} onClick={() => setFilterRegion(r)}
              className={`px-3 py-1 text-xs rounded-lg transition-colors ${filterRegion === r ? 'bg-[#0a1f3a] text-white' : isDark ? 'bg-[#1e2a3d] text-[#94a3b8]' : 'bg-[#e9eff7] text-[#6b7684]'}`}>
              {r === 'all' ? '전체' : r}
            </button>
          ))}
        </div>
      </div>

      {/* 모바일 */}
      <div className="md:hidden flex flex-col" style={{ height: MOBILE_H }}>
        <div ref={mobileListRef} className={`border-b ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}
          style={{ height: '22%', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <div className="flex gap-2 px-3 py-2 h-full items-stretch">
            {filteredSpots.map(s => <SeaCard key={s.id} spot={s} compact />)}
          </div>
        </div>
        <div style={{ height: '78%', overflow: 'hidden' }}>
          <SeaMap spots={filteredSpots} selectedSpotId={selectedSpotId} onSpotSelect={setSelectedSpotId} center={mapCenter} level={selectedSpotId ? 7 : 8} />
        </div>
      </div>

      {/* 데스크탑 */}
      <div className="hidden md:flex flex-row max-w-7xl mx-auto" style={{ height: DESKTOP_H }}>
        <div ref={desktopListRef} className={`w-[400px] flex-shrink-0 overflow-y-auto border-r ${isDark ? 'border-[#2c3a52]' : 'border-[#e9eff7]'}`}>
          <div className="p-3 space-y-2">
            {filteredSpots.map(s => <SeaCard key={s.id} spot={s} />)}
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <SeaMap spots={filteredSpots} selectedSpotId={selectedSpotId} onSpotSelect={setSelectedSpotId} center={mapCenter} level={selectedSpotId ? 7 : 8} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────────
const TAB_LABELS: Record<MapTab, string> = {
  pool: '풀장지도',
  valley: '계곡지도',
  sea: '바다지도',
}

export default function MapPage() {
  const [activeTab, setActiveTab] = useState<MapTab>('pool')
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    if (tab === 'valley' || tab === 'sea' || tab === 'pool') {
      setActiveTab(tab)
    }
  }, [])

  return (
    <div className={`${isDark ? 'bg-[#0f172a]' : 'bg-[#f4f7fb]'}`}>
      {/* ── 탭 바 ── */}
      <div className={`sub-sticky z-20 border-b backdrop-blur-xl ${
        isDark ? 'bg-[#0f172a]/95 border-[#2c3a52]' : 'bg-[#f4f7fb]/95 border-[#e9eff7]'
      }`}>
        <div className="flex items-center justify-between px-3 sm:px-4 max-w-2xl mx-auto w-full gap-2">
          {/* 탭 */}
          <div className="flex items-center gap-0 flex-1">
            {(['pool','valley','sea'] as MapTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-xs sm:text-sm font-semibold transition-all whitespace-nowrap border-b-2 ${
                  activeTab === tab
                    ? isDark
                      ? 'border-[#5ea6ff] text-[#e8f0fb]'
                      : 'border-[#0a1f3a] text-[#0a1f3a]'
                    : isDark
                      ? 'border-transparent text-[#94a3b8]'
                      : 'border-transparent text-[#6b7684]'
                }`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
          {/* 오른쪽: 제보 버튼 (계곡·바다 탭만) */}
          <div className="flex-shrink-0">
            {(activeTab === 'valley' || activeTab === 'sea') ? (
              <Link
                href="/spots/submit"
                className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap shadow-[inset_0_0_0_1px_currentColor] ${
                  isDark
                    ? 'text-[#5ea6ff]'
                    : 'text-[#2b6df0]'
                }`}
              >
                <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden xs:inline">포인트 제보</span>
                <span className="xs:hidden">제보</span>
              </Link>
            ) : (
              <div className="w-8" />
            )}
          </div>
        </div>
      </div>

      {/* ── 탭 콘텐츠 ── */}
      <KakaoMapLoader>
        {activeTab === 'pool'   && <PoolTab   isDark={isDark} />}
        {activeTab === 'valley' && <ValleyTab isDark={isDark} />}
        {activeTab === 'sea'    && <SeaTab    isDark={isDark} />}
      </KakaoMapLoader>
    </div>
  )
}
